<script setup lang="ts">
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogTrigger,
    } from '@/components/ui/alert-dialog';
    import { useI18n } from 'vue-i18n';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';
    import { toast } from 'vue-sonner';
    import type { Payroll } from '@/types';
    import { Button } from '@/components/ui/button';
    import { Minus } from 'lucide-vue-next';
    import { router } from '@inertiajs/vue3';
    import { ref, onMounted, onUnmounted, computed } from 'vue';

    const { t } = useI18n();

    const props = defineProps<{
        payroll: Payroll;
        timeSinceApproval: number;
        isClient: boolean;
    }>();

    const timeLeft = ref(Math.max(60 - Math.floor(props.timeSinceApproval), 0));
    let interval: ReturnType<typeof setInterval>;

    onMounted(() => {
        interval = setInterval(() => {
            if (timeLeft.value > 0) {
                timeLeft.value--;
            } else {
                clearInterval(interval);
            }
        }, 60000);
    });

    onUnmounted(() => {
        clearInterval(interval);
    });

    const isUnlockable = computed(() => {
        if (!props.isClient) return true;
        return timeLeft.value > 0;
    });

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            axios.post(
                route('v2.salary.payrolls.unlock', {
                    payroll: props.payroll.id,
                }),
            ),
        onSuccess: (data) => {
            toast.success(data.data.success || t('forms.general.success'));
            router.reload();
        },
        onError: (error: any) => {
            console.log('error', error);
            toast.error(error.response.data.error || t('forms.system_error'));
        },
    });
</script>

<template>
    <AlertDialog>
        <AlertDialogTrigger as-child>
            <Button
                variant="ghost_destructive"
                size="xs"
                width="narrow"
                :disabled="!isUnlockable"
            >
                {{ t('payrolls.edit.unlock') }}
                <span v-if="isClient">({{ timeLeft }} {{ t('payrolls.edit.mintues_short') }})</span>
                <Minus class="text-destructive" />
            </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t('payrolls.unlock.title') }}</AlertDialogTitle>
                <AlertDialogDescription>
                    {{ t('payrolls.unlock.confirm') }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t('payrolls.unlock.no') }}</AlertDialogCancel>
                <AlertDialogAction
                    variant="default"
                    @click="mutate"
                >
                    {{ t('payrolls.unlock.yes') }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
